import { CheckmarkOutline, MisuseOutline } from '@carbon/icons-react';
import { keyframes } from '@emotion/css';
import ButtonClose, { ButtonCloseVariants } from '@features/ui-form/components/Button/ButtonClose';
import { Theme } from '@mui/joy';
import { styled } from '@mui/joy/styles';
import { useViewport } from '@root/src/utils/hooks/useViewportSize';
import { XOR } from '@root/src/utils/types/XOR';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import styleToCss from 'style-object-to-css-string';

export enum ToastVariants {
  SUCCESS,
  ERROR,
}

export type ToastProps = {
  onClose?: () => void;
  title: string;
  variant?: ToastVariants;
} & XOR<{ message?: string }, { children?: ReactNode }>;

type ToastifyProps = {
  closeToast?: () => void;
};

const Toast = ({
  title,
  message,
  children,
  variant = ToastVariants.SUCCESS,
  onClose,
  // Currently unused, it'as actually used in showToast
  closeToast,
}: ToastProps & ToastifyProps): JSX.Element => {
  const [isVisible, setIsVisible] = useState<boolean>(true);

  const onCloseWrapper = useCallback(() => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
    if (closeToast) {
      closeToast();
    }
  }, [closeToast, onClose]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onCloseWrapper(), 500);
    }, 3000);

    return () => clearTimeout(timer);
  }, [onCloseWrapper]);

  const width = useViewport();

  return (
    <ToastContainer variant={variant} isVisible={isVisible} size={width}>
      <TopBar>
        <ToastTitle>
          {variant === ToastVariants.SUCCESS ? <CheckmarkOutline size={20} /> : <MisuseOutline size={20} />}
          <span>{title}</span>
        </ToastTitle>
        {(onClose || closeToast) && <ButtonClose onClick={onCloseWrapper} variant={ButtonCloseVariants.TOAST} />}
      </TopBar>
      <ToastContent>{message ?? children}</ToastContent>
    </ToastContainer>
  );
};

export default Toast;

type StyleMap = {
  [variant in ToastVariants]: (theme: Theme) => string;
};

const variantStyleMap: StyleMap = {
  [ToastVariants.ERROR]: theme => `
    background-color: ${theme.vars.palette.hoverBrown};
  `,
  [ToastVariants.SUCCESS]: theme => `
    background-color: ${theme.vars.palette.greenColor};
  `,
};

const getVariantStyle = ({ theme, variant }: { theme: Theme; variant: ToastVariants }) =>
  // eslint-disable-next-line security/detect-object-injection
  variantStyleMap[variant](theme);

const TopBar = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: start;
`;

const slideInFromRight = keyframes`
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOutToRight = keyframes`
  0% {
    transform: translateX(0);
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled('div')<{ variant: ToastVariants; isVisible: boolean; size: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: absolute;
  bottom: ${({ size, theme }) => (size < theme.breakpoints.values.tablet ? '60px' : '62px')};
  right: ${({ size, theme }) => (size < theme.breakpoints.values.tablet ? '0px' : '20px')};
  padding: 16px;
  border-radius: 2px;

  ${getVariantStyle};

  z-index: var(--z-index-toast);
  animation: ${({ isVisible }) => (isVisible ? slideInFromRight : slideOutToRight)} 0.5s ease forwards;
`;

const ToastContent = styled('div')`
  ${({ theme }) => styleToCss(theme.typography['body-md'])};
  color: white;
`;

const ToastTitle = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  ${({ theme }) => styleToCss(theme.typography['h2-sans-serif'])};
  color: white;
`;
